import { ArcAutomationRunStatus, ArcAutomationTriggerType } from '../../interfaces/atlasai'
import { FilterFieldType, IFilter } from './types'

// Shared between the filter config below and the Recent Run / Run Health cell
// rendering in AutomationListPage.tsx, so the filter's option labels always
// match what's shown on the chip/dots.
export const ARC_AUTOMATION_RUN_STATUS_OPTIONS = [
  { value: ArcAutomationRunStatus.COMPLETED, label: 'Succeeded' },
  { value: ArcAutomationRunStatus.FAILED, label: 'Failed' },
  { value: ArcAutomationRunStatus.RUNNING, label: 'Running' },
  { value: ArcAutomationRunStatus.PENDING, label: 'Pending' },
]

/**
 * Filter + sort config for the Arc Automations list page.
 *
 * Column order here drives column order in the table (via TableFiltersRow).
 * Body cells must be rendered in the same order. The 'owner' column is only
 * ever shown/filterable for admins (gated client-side in AutomationListPage —
 * non-admins are already server-scoped to their own automations, so it's
 * meaningless for them); it stays in this shared array because the server
 * needs it to build the WHERE clause.
 */
export const ARC_AUTOMATIONS_FILTERS_CONFIG = [
  {
    field: 'name',
    dbField: 'name',
    alias: 'automation',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Name',
  },
  {
    // Display is still the human-readable schedule/event label, but the
    // filter itself narrows on the underlying trigger type.
    field: 'trigger',
    dbField: 'trigger_type',
    alias: 'automation',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Trigger',
    options: [
      { value: ArcAutomationTriggerType.SCHEDULE, label: 'Schedule' },
      { value: ArcAutomationTriggerType.EVENT, label: 'Event' },
    ],
  },
  {
    field: 'isActive',
    dbField: 'is_active',
    alias: 'automation',
    type: FilterFieldType.Boolean,
    isSortable: true,
    title: 'Status',
    options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Paused' },
    ],
  },
  {
    field: 'nextRunAt',
    dbField: 'next_run_at',
    alias: 'automation',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Next Run',
    align: 'right',
  },
  {
    field: 'lastRunAt',
    dbField: 'last_run_at',
    alias: 'automation',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last Run',
    align: 'right',
  },
  {
    // Not a real column — it's the status of each automation's most recent
    // run, joined in via the runs[] relation. isCustom tells Base.buildFiltersQuery
    // to skip generating a WHERE clause; List.ts applies it manually via a
    // correlated subquery. Also doubles as the filter for the Run Health
    // column (finding a failing automation only needs "is the latest run
    // failed", not "did any of the last 7 fail").
    field: 'recentRunStatus',
    type: FilterFieldType.List,
    isCustom: true,
    isMultiple: true,
    title: 'Recent Run',
    align: 'right',
    options: ARC_AUTOMATION_RUN_STATUS_OPTIONS,
  },
  {
    // Display-only — a row of dots for the last 7 runs. Filtering "is this
    // automation failing" is covered by the recentRunStatus filter above.
    field: 'runHealth',
    type: FilterFieldType.Text,
    title: 'Run Health',
    excludeFilter: true,
    align: 'right',
  },
  {
    field: 'owner',
    dbField: 'user_id',
    alias: 'automation',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Owner',
    align: 'right',
    // Populated at render time from the inviteable-users list (see
    // AutomationListPage.tsx) — this static array only fixes the field's
    // shape for validation/query building.
    options: [] as { value: string; label: string }[],
  },
] as const satisfies IFilter[]
